import { RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { setChat, setUpdateChat } from '../Store/features/chat/chatSlice';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import { setDragZoneInputProps, setDragZoneRootProps } from '../Store/features/environment/environmentSlice';
import { Chat, ChatList } from '../Types';
import { DLPState } from '../Types/Enums';
import useSocketMessageHandler from './useSocketMessageHandler';
import useClipboard from './useClipboard';
import useDocuments from './useDocuments';
import useDragAndDrop from './useDragDrop';
import { chatService } from '../Services/chatService';
import { settingsService } from '../Services/settingsService';
import usePromptState from './state/usePromptState';
import { useStoreInputMessage } from './useStoreInputMessage';
import { getBreakpoint } from '../utils/breakpoint';
import { selectIsSendingByChatId, selectIsStopableByChatId } from '../Store/selectors/chatSelectors';
import toast from 'react-hot-toast';

export const MAX_MESSAGE_LENGTH = 50000;

interface ChatInputProps {
    chat?: Chat;
    initMessage?: string;
}

const DEFAULT_CHAT = {} as Chat;

const useChatInput = ({ chat = DEFAULT_CHAT, initMessage }: ChatInputProps) => {
    const dispatch = useAppDispatch();
    const currentURL = useAppSelector((state: RootState) => state.environment.currentURL);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const windowWidth = useAppSelector((state: RootState) => state.environment.windowWidth);
    const { basePrompt, selectedPrompt } = usePromptState();
    const { storeInputMessage, getStoredInputData } = useStoreInputMessage();

    const { SocketMessageHandlerActions } = useSocketMessageHandler();

    const isPreviewPrompt = useMemo(
        () => !currentURL.includes('chat') && !currentURL.endsWith('/') && !currentURL.includes('/start'),
        [currentURL],
    );
    const isHome = useMemo(() => currentURL.endsWith('/') || currentURL.includes('/start'), [currentURL]);

    const isStopable = useAppSelector((state: RootState) => selectIsStopableByChatId(state, chat?.identifier));
    const isSending = useAppSelector((state: RootState) => selectIsSendingByChatId(state, chat?.identifier));

    const chats = useAppSelector((state: RootState) => state.chatlist.chatlist as ChatList[]);
    const featuresDocumentUpload = useAppSelector(
        (state: RootState) => state.settings.features.documentUpload as boolean,
    );
    const defaultModel = useAppSelector((state: RootState) => state.settings.defaultModel as string);

    const [images, setImages] = useState<{ url: string; sha1: string }[]>([]);
    const [files, setFiles] = useState<{ name: string; url: string; sha1: string }[]>([]);
    const [tmpImageUrls, setTmpImageUrls] = useState<string[]>([]);
    const [tmpFileNames, setTmpFileNames] = useState<string[]>([]);
    const [fileTypes, setFileTypes] = useState<{ [key: string]: string[] }>({});
    const [allMimeTypesWithFileExtensions, setAllMimeTypesWithFileExtensions] = useState<{ [key: string]: string[] }>(
        {},
    );

    const [fileHoverIndex, setFileHoverIndex] = useState<number>(-1);
    const [imageHoverIndex, setImageHoverIndex] = useState<number>(-1);
    const [newMessage, setNewMessage] =
        useState<string>(initMessage ?? getStoredInputData(chat.identifier).message) ?? '';
    const [numOfLines, setNumOfLines] = useState<number>(0);
    const [showIcons, setShowIcons] = useState<boolean>(false);
    const [showExamples, setShowExamples] = useState<boolean>(false);
    const [userTyped, setUserTyped] = useState<boolean>(false);

    const imageUploadButtonRef: RefObject<HTMLInputElement> = useRef(null!);
    const textInputRef: RefObject<HTMLTextAreaElement> = useRef(null!);
    const ToggleInputFeaturesRef: RefObject<HTMLDivElement> = useRef(null!);
    const timerId = useRef<NodeJS.Timeout | null>(null);
    const prevChatIdentifier = useRef<string>(chat.identifier);
    const isRestoringData = useRef<boolean>(false);

    const [height, setHeight] = useState<number>(textInputRef?.current?.clientHeight || 44);
    const [ToggleInputFeaturesWidth, setToggleInputFeaturesWidth] = useState<number>(80);

    const [showPiiText, setShowPiiText] = useState(false);
    const [originalMessage, setOriginalMessage] = useState('');
    const [shouldSendAfterDlpChange, setShouldSendAfterDlpChange] = useState(false);
    const [fullDlpBlocked, setFullDlpBlocked] = useState(false);
    const [isDlpChecking, setIsDlpChecking] = useState(false);

    const { DocumentsActions } = useDocuments({
        setFiles,
        setTmpFileNames,
        textInputRef,
        setImages,
        setTmpImageUrls,
        model: selectedPrompt,
        i18n,
        fileTypes,
    });

    const { ClipboardActions } = useClipboard({
        setTmpFileNames,
        setFiles,
        setNewMessage,
        textInputRef,
        fileTypes,
        allowedMimeTypes: selectedPrompt?.allowedMimeTypes,
    });

    const { getRootProps, getInputProps } = useDragAndDrop({
        fileTypes,
        dispatch,
        chatPrompt: selectedPrompt!,
        selectedPromptIdentifier: selectedPrompt!.identifier,
        setTmpFileNames,
        setFiles,
        i18n,
        combinedDlp: selectedPrompt?.combinedDlp ?? DLPState.dlp_on,
        allowedMimeTypes: selectedPrompt?.allowedMimeTypes,
    });

    const handleDlpActiveCheckbox = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (!isHome) {
                dispatch(setChat({ ...chat, dlp_active: event.target.checked ? true : false }));
                dispatch(setUpdateChat(true));
            }
        },
        [isHome, dispatch, chat],
    );

    const stopStream = useCallback(async () => {
        if (chat?.identifier) {
            SocketMessageHandlerActions.stopStream(chat.identifier);
        }
    }, [SocketMessageHandlerActions, chat]);

    const handleCrossClick = useCallback(() => {
        setShowIcons(false);
        textInputRef.current?.focus();
    }, []);

    const handleFileChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>, resetFileInput: () => void) => {
            await DocumentsActions.handleFileChange(
                event,
                selectedPrompt!.identifier,
                selectedPrompt!.combinedDlp ?? DLPState.dlp_on,
            );
            resetFileInput();
        },
        [DocumentsActions, selectedPrompt],
    );

    const handleFileDelete = useCallback(
        (index: number) => {
            DocumentsActions.handleFileDelete(index);
        },
        [DocumentsActions],
    );

    const handleImageChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            DocumentsActions.handleImageChange(event);
        },
        [DocumentsActions],
    );

    const handleImageDelete = useCallback(
        (index: number) => {
            DocumentsActions.handleImageDelete(index);
        },
        [DocumentsActions],
    );

    const handleInputChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNewMessage(() => event.target.value);
        setShowPiiText(false);
        if (event.target.value.length > 0) {
            setShowIcons(false);
        } else {
            setShowIcons(true);
        }
    }, []);

    const handleDlpChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        textInputRef.current?.focus();
    }, []);

    const handlePaste = useCallback(
        (event: React.ClipboardEvent) => {
            ClipboardActions.handlePaste(
                event,
                newMessage,
                selectedPrompt!.combinedDlp ?? DLPState.dlp_on,
                fileTypes,
                selectedPrompt?.chatUploadEnabled ?? false,
            );
        },
        [ClipboardActions, newMessage, selectedPrompt, fileTypes],
    );

    const handlePlusClick = useCallback(() => {
        setShowIcons(true);
        textInputRef.current?.focus();
    }, []);

    const sendMessageHandler = useCallback(
        (example?: string, dlp_active: boolean = true) => {
            SocketMessageHandlerActions.sendMessage(
                chat.identifier ?? '',
                example ?? newMessage,
                selectedPrompt!.identifier ?? basePrompt!.identifier,
                chats,
                dlp_active,
                images,
                files,
                [
                    () => setNewMessage(''),
                    () => setImages([]),
                    () => setTmpImageUrls([]),
                    () => setFiles([]),
                    () => setTmpFileNames([]),
                    () => setShowIcons(true),
                    () => textInputRef.current?.focus(),
                ],
            );
            setShowIcons(true);
        },
        [chat, SocketMessageHandlerActions, newMessage, images, files, selectedPrompt, chats, basePrompt],
    );

    const handleDlpSendMessage = useCallback(
        async (overrideMessage?: string) => {
            if (isSending || isDlpChecking) return;
            const messageToSend = overrideMessage ?? newMessage;
            if (messageToSend.length > MAX_MESSAGE_LENGTH) return;
            setFullDlpBlocked(false);
            if (selectedPrompt!.combinedDlp === DLPState.dlp_optional) {
                setIsDlpChecking(true);
                try {
                    const messageToCheck = overrideMessage ? overrideMessage : newMessage;
                    setOriginalMessage(messageToCheck);
                    const response = await chatService.checkDlp(messageToCheck, chat?.identifier || '');

                    if (response.data.error) {
                        setNewMessage('〖' + response.data.error + '⟆Regex〗');
                        setShowPiiText(true);
                        if (textInputRef.current) {
                            textInputRef.current.value = '〖' + response.data.error + '⟆Regex〗';
                            textInputRef.current.blur();
                        }
                        setFullDlpBlocked(true);
                        return;
                    }

                    if (response.data.text && response.data.text !== messageToCheck) {
                        setNewMessage(response.data.text);
                        setShowPiiText(true);
                        if (textInputRef.current) {
                            textInputRef.current.value = response.data.text;
                            textInputRef.current.blur();
                        }
                        return;
                    }
                } catch (error) {
                    return;
                } finally {
                    setIsDlpChecking(false);
                }
            }
            setShowPiiText(false);
            setNewMessage(messageToSend);
            if (textInputRef.current) {
                textInputRef.current.value = originalMessage;
            }
            if (overrideMessage) {
                sendMessageHandler(overrideMessage, selectedPrompt!.combinedDlp !== DLPState.dlp_off);
            } else {
                sendMessageHandler(undefined, selectedPrompt!.combinedDlp !== DLPState.dlp_off);
            }
        },
        [
            newMessage,
            chat?.identifier,
            sendMessageHandler,
            originalMessage,
            textInputRef,
            isSending,
            isDlpChecking,
            selectedPrompt,
        ],
    );

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' && !event.shiftKey && windowWidth > getBreakpoint('sm')) {
                event.preventDefault();
                if (newMessage !== '' && newMessage.length > MAX_MESSAGE_LENGTH) {
                    toast.error(
                        i18n.translate({
                            id: 'toast.messageLengthLimit',
                            sourceName: 'Chat',
                            params: { current: newMessage.length, max: MAX_MESSAGE_LENGTH },
                        }),
                    );
                    return;
                }
                newMessage !== '' && newMessage.length <= MAX_MESSAGE_LENGTH && handleDlpSendMessage();
            }
        },
        [handleDlpSendMessage, windowWidth, getBreakpoint, newMessage, i18n],
    );

    const handleExampleClick = useCallback(
        (example: string) => {
            handleDlpSendMessage(example);
        },
        [handleDlpSendMessage],
    );

    const calculateWidth = useCallback(() => {
        if (newMessage.length === 0 || showIcons) {
            return ToggleInputFeaturesWidth;
        }

        if ((newMessage.length !== 0 && !showIcons) || (showIcons && newMessage.length !== 0)) {
            return 40;
        }

        return 0;
    }, [ToggleInputFeaturesWidth, showIcons, newMessage]);

    useEffect(() => {
        if (!chat.identifier) return;
        storeInputMessage(chat.identifier, newMessage, images, files);
    }, [newMessage, images, files, chat.identifier, storeInputMessage]);

    useEffect(() => {
        if (!chat.identifier) return;
        if (prevChatIdentifier.current !== chat.identifier) {
            isRestoringData.current = true;
            const storedData = getStoredInputData(chat.identifier);
            setNewMessage(storedData.message);
            setImages(storedData.images);
            setFiles(storedData.files);
            setTmpImageUrls(storedData.images.map((img: any) => img.url));
            setTmpFileNames(storedData.files.map((file: any) => file.name));
            prevChatIdentifier.current = chat.identifier;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chat.identifier]);

    useEffect(() => {
        if (Object.keys(allMimeTypesWithFileExtensions).length === 0) return;

        const fileTypes =
            selectedPrompt!.allowedMimeTypes?.reduce((acc: { [key: string]: string[] }, mimeType) => {
                if (allMimeTypesWithFileExtensions[mimeType]) {
                    return {
                        ...acc,
                        [mimeType.toLowerCase()]: allMimeTypesWithFileExtensions[mimeType].map(
                            (ext: string) => '.' + ext,
                        ),
                    };
                }
                return acc;
            }, {}) || {};

        setFileTypes(fileTypes);
    }, [selectedPrompt, allMimeTypesWithFileExtensions]);

    const fetchMimeTypes = useCallback(() => {
        const model = selectedPrompt?.model ?? defaultModel;
        if (!model) return;
        const mode = selectedPrompt?.documentProcessingMode ?? null;
        settingsService.getAllMimeTypesAndFileExtensions(model, mode).then((response) => {
            const raw = response.data?.value ?? response.data;
            const data: { [key: string]: string[] } =
                raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
            setAllMimeTypesWithFileExtensions(data);
        });
    }, [selectedPrompt?.model, selectedPrompt?.documentProcessingMode, defaultModel]);

    useEffect(() => {
        fetchMimeTypes();
    }, [fetchMimeTypes, currentURL]);

    useEffect(() => {
        const model = selectedPrompt?.model ?? defaultModel;
        if (isHome && model && Object.keys(allMimeTypesWithFileExtensions).length === 0) {
            fetchMimeTypes();
        }
    }, [isHome, selectedPrompt?.model, defaultModel, allMimeTypesWithFileExtensions, fetchMimeTypes]);

    useEffect(() => {
        setUserTyped(false);
        setShowExamples(false);
    }, [selectedPrompt, chat.identifier]);

    useEffect(() => {
        if (timerId.current) {
            clearTimeout(timerId.current);
        }

        if (!userTyped) {
            timerId.current = setTimeout(() => {
                setShowExamples(true);
            }, 3000);
        }

        return () => {
            if (timerId.current) {
                clearTimeout(timerId.current);
            }
        };
    }, [userTyped, selectedPrompt]);

    useEffect(() => {
        if (newMessage.length > 0) {
            setUserTyped(true);
            setShowExamples(false);
        }
    }, [newMessage]);

    useEffect(() => {
        if (textInputRef.current) {
            const updateInputHeight = () => {
                if (textInputRef.current) {
                    const lineHeight = parseFloat(window.getComputedStyle(textInputRef.current).lineHeight);
                    const numberOfLines = Math.floor(textInputRef.current.scrollHeight / lineHeight);
                    setNumOfLines(numberOfLines);

                    const newHeight = textInputRef.current.getBoundingClientRect().height;
                    setHeight(newHeight);
                }
            };

            updateInputHeight();

            const resizeObserver = new ResizeObserver(() => {
                updateInputHeight();
            });

            resizeObserver.observe(textInputRef.current);

            return () => {
                if (textInputRef) {
                    resizeObserver.disconnect();
                }
            };
        }
    }, [newMessage, windowWidth]);

    useLayoutEffect(() => {
        let featureContainerWidth = 0;
        const updateWidth = () => {
            if (ToggleInputFeaturesRef.current) {
                featureContainerWidth = 0;
                Array.from(ToggleInputFeaturesRef.current.children).forEach((child: Element) => {
                    featureContainerWidth += child.getBoundingClientRect().width;
                });
                setToggleInputFeaturesWidth(featureContainerWidth);
            }
        };

        const observer = new ResizeObserver(() => {
            updateWidth();
        });

        if (ToggleInputFeaturesRef.current) {
            Array.from(ToggleInputFeaturesRef.current.children).forEach((child: Element) => {
                observer.observe(child);
            });
        }

        updateWidth();

        return () => {
            observer.disconnect();
        };
    }, [ToggleInputFeaturesRef, showIcons, newMessage, chat.dlp]);

    useEffect(() => {
        if (!isPreviewPrompt) {
            textInputRef.current?.focus();
        }
    }, [chat.identifier, selectedPrompt, chat.dlp_active, isHome, isPreviewPrompt]);

    useEffect(() => {
        if (Object.keys(fileTypes).length > 0 && selectedPrompt?.chatUploadEnabled) {
            dispatch(setDragZoneRootProps(() => getRootProps()));
            dispatch(setDragZoneInputProps(() => getInputProps()));
        } else {
            dispatch(setDragZoneRootProps(() => {}));
            dispatch(setDragZoneInputProps(() => {}));
        }
    }, [getRootProps, getInputProps, dispatch, fileTypes, selectedPrompt?.chatUploadEnabled]);

    const ChatInputActions = useMemo(
        () => ({
            handleKeyDown,
            sendMessageHandler,
            handleInputChange,
            handleImageChange,
            handleImageDelete,
            handleFileChange,
            handleFileDelete,
            handlePlusClick,
            handleCrossClick,
            handlePaste,
            handleDlpChange,
            stopStream,
            handleDlpActiveCheckbox,
            handleExampleClick,
            calculateWidth,
            getBreakpoint,
            handleDlpSendMessage,
        }),
        [
            handleKeyDown,
            sendMessageHandler,
            handleInputChange,
            handleImageChange,
            handleImageDelete,
            handleFileChange,
            handleFileDelete,
            handlePlusClick,
            handleCrossClick,
            handlePaste,
            handleDlpChange,
            stopStream,
            handleDlpActiveCheckbox,
            handleExampleClick,
            calculateWidth,
            getBreakpoint,
            handleDlpSendMessage,
        ],
    );

    const sendMessage = useCallback(() => {
        setNewMessage(originalMessage);
        if (textInputRef.current) {
            textInputRef.current.value = originalMessage;
        }
        sendMessageHandler(originalMessage);
        setNewMessage('');
        if (textInputRef.current) {
            textInputRef.current.value = '';
        }
    }, [sendMessageHandler, originalMessage, textInputRef, setNewMessage]);

    const sendOriginalMessage = useCallback(() => {
        setNewMessage(originalMessage);
        if (textInputRef.current) {
            textInputRef.current.value = originalMessage;
        }
        sendMessageHandler(originalMessage, false);
        setNewMessage('');
        if (textInputRef.current) {
            textInputRef.current.value = '';
        }
    }, [sendMessageHandler, originalMessage, textInputRef, setNewMessage]);

    const handleTextareaClick = useCallback(() => {
        if (showPiiText) {
            setNewMessage(originalMessage);
            if (textInputRef.current) {
                textInputRef.current.value = originalMessage;
                textInputRef.current.focus();
            }
            setShowPiiText(false);
        }
    }, [showPiiText, originalMessage, textInputRef]);

    useEffect(() => {
        if (shouldSendAfterDlpChange) {
            sendOriginalMessage();
            setShouldSendAfterDlpChange(false);
        }
    }, [shouldSendAfterDlpChange, sendOriginalMessage]);

    return {
        ChatInputActions,
        isPreviewPrompt,
        tmpImageUrls,
        setImageHoverIndex,
        imageHoverIndex,
        handleImageDelete,
        tmpFileNames,
        files,
        fileHoverIndex,
        setFileHoverIndex,
        handleFileDelete,
        newMessage,
        setNewMessage,
        numOfLines,
        showIcons,
        ToggleInputFeaturesRef,
        ToggleInputFeaturesWidth,
        showExamples,
        imageUploadButtonRef,
        selectedPrompt,
        i18n,
        textInputRef,
        height,
        isSending,
        isStopable,
        isDlpChecking,
        featuresDocumentUpload,
        showPiiText,
        setShowPiiText,
        originalMessage,
        shouldSendAfterDlpChange,
        setShouldSendAfterDlpChange,
        fullDlpBlocked,
        sendMessage,
        handleDlpSendMessage,
        handleTextareaClick,
        fileTypes,
        windowWidth,
    };
};

export default useChatInput;
